export const BANNER_COLORS = ['amber', 'red', 'blue', 'green', 'purple'];

export const bannerStatus = (banner, nowMs) => {
  if (Date.parse(banner.starts_at) > nowMs) return 'scheduled';
  if (banner.ends_at && Date.parse(banner.ends_at) <= nowMs) return 'ended';
  return 'live';
};

export const windowsOverlap = (a, b) => {
  const aStart = Date.parse(a.starts_at);
  const aEnd = a.ends_at ? Date.parse(a.ends_at) : Infinity;
  const bStart = Date.parse(b.starts_at);
  const bEnd = b.ends_at ? Date.parse(b.ends_at) : Infinity;
  return aStart < bEnd && bStart < aEnd;
};

export const isValidHttpUrl = (str) => {
  try {
    return ['http:', 'https:'].includes(new URL(str).protocol);
  } catch {
    return false;
  }
};

export const serverOffsetMs = (serverTimeIso) => Date.parse(serverTimeIso) - Date.now();

// Client-side mirror of app/Helpers/HtmlSanitizer.php: only b/i/u and
// a[href^=http(s)] survive, emitted as canonical tags. Additionally maps
// style-based formatting (Word/GDocs paste spans like font-weight:700) onto
// those tags, and turns block boundaries and <br> into \n — the banner body
// renders with white-space: pre-line. The server sanitizer remains the
// security boundary; this exists so pasted formatting survives the editor.
const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'dd', 'div', 'dl', 'dt',
  'fieldset', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'li', 'ol', 'p', 'pre', 'section', 'table', 'td', 'th', 'tr', 'ul'
]);

const escapeText = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// GDocs wraps copies in <b style="font-weight:normal"> — explicit non-bold
// weight overrides the tag.
const isBold = (el, tag) => {
  const fw = el.style.fontWeight;
  if (/^(normal|[1-5]00)$/.test(fw)) return false;
  return ['b', 'strong'].includes(tag) || /^(bold|bolder|[6-9]00)$/.test(fw);
};
const isItalic = (el, tag) => ['i', 'em'].includes(tag) || el.style.fontStyle === 'italic';
const isUnderline = (el, tag) =>
  tag === 'u' || `${el.style.textDecoration} ${el.style.textDecorationLine}`.includes('underline');

const serializeNode = (node, ctx) => {
  let out = '';
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += escapeText(child.nodeValue.replace(/\s+/g, ' '));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    const tag = child.nodeName.toLowerCase();
    if (tag === 'script' || tag === 'style') continue;
    if (tag === 'br') {
      out += '\n';
      continue;
    }

    const bold = !ctx.bold && isBold(child, tag);
    const italic = !ctx.italic && isItalic(child, tag);
    const underline = !ctx.underline && isUnderline(child, tag);
    const href = tag === 'a' && !ctx.link ? (child.getAttribute('href') || '').trim() : '';
    const link = /^https?:\/\//i.test(href);

    let inner = serializeNode(child, {
      bold: ctx.bold || bold,
      italic: ctx.italic || italic,
      underline: ctx.underline || underline,
      link: ctx.link || link
    });

    if (inner.trim() !== '') {
      if (underline) inner = `<u>${inner}</u>`;
      if (italic) inner = `<i>${inner}</i>`;
      if (bold) inner = `<b>${inner}</b>`;
      if (link) inner = `<a href="${href.replace(/"/g, '&quot;')}">${inner}</a>`;
    }
    out += BLOCK_TAGS.has(tag) ? `${inner}\n` : inner;
  }
  return out;
};

export const sanitizeRichHtml = (html) => {
  const body = new DOMParser().parseFromString(html || '', 'text/html').body;
  return serializeNode(body, {})
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/ {2,}/g, ' ')
    .trim();
};

// Visible-character count of a body string — mirrors HtmlSanitizer::textLength.
export const richTextLength = (html) => {
  const el = document.createElement('div');
  el.innerHTML = html || '';
  return (el.textContent || '').length;
};

/** The portal pages a banner's button may open (Hunter, 2026-10-01): the server accepts exactly these paths. */
export const PORTAL_DESTINATIONS = [
  { path: '/hub', label: 'Home' },
  { path: '/hub/messages', label: 'Messages' },
  { path: '/hub/library', label: 'Library' },
  { path: '/hub/sessions', label: 'Sessions' },
  { path: '/hub/details', label: 'My details' },
];
